const SHORTCODE = /^[A-Za-z0-9_-]{5,64}$/;
const ASSET_NAME = /^[A-Za-z0-9][A-Za-z0-9_.-]*\.(?:jpe?g|png|webp)$/i;
const FRESH_FOR = 48 * 60 * 60 * 1000;
const DISMISS_PREFIX = 'nlv-floating-instagram-dismissed:';

export function instagramPermalink(value, expectedId) {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || !['instagram.com', 'www.instagram.com'].includes(url.hostname) || url.port || url.username || url.password || url.search || url.hash) return null;
    const parts = url.pathname.match(/^\/(reel|p)\/([A-Za-z0-9_-]+)\/?$/);
    if (!parts || !SHORTCODE.test(parts[2]) || parts[2] !== expectedId) return null;
    return `https://www.instagram.com/${parts[1]}/${parts[2]}/`;
  } catch { return null; }
}

export function hostedInstagramVideo(value) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && !url.port && !url.username && !url.password && !url.hash && /^\/videos\/[A-Za-z0-9/_-]+\.mp4$/.test(url.pathname) ? url.href : null;
  } catch { return null; }
}

export function instagramFeedUrl(value, origin) {
  try {
    const url = new URL(value, origin);
    const local = url.origin === new URL(origin).origin;
    if ((!local && !(url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && !url.port)) || url.username || url.password || url.hash || !url.pathname.endsWith('/assets/instagram-feed.json')) return null;
    return url.href;
  } catch { return null; }
}

export function instagramFeedTimestamp(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(value)) return NaN;
  const time = Date.parse(value);
  if (!Number.isFinite(time) || new Date(time).toISOString().replace(/\.\d{3}Z$/, 'Z') !== value.replace(/\.\d{1,3}Z$/, 'Z')) return NaN;
  return time;
}

export function validateInstagramFeed(raw, now = Date.now()) {
  if (!raw || raw.schema_version !== 1 || raw.enabled !== true || raw.account !== 'nolimit.vtg' || !Array.isArray(raw.posts) || raw.posts.length > 24) return null;
  const checked = instagramFeedTimestamp(raw.checked_at);
  const fresh = Number.isFinite(checked) && checked <= now + 300000 && now - checked <= FRESH_FOR;
  for (let i = 0; i < raw.posts.length; i++) {
    const post = raw.posts[i];
    if (!post || typeof post.id !== 'string' || typeof post.cover_asset !== 'string' || !SHORTCODE.test(post.id) || !ASSET_NAME.test(post.cover_asset) || post.cover_asset.includes('..')) continue;
    const permalink = instagramPermalink(post.permalink, post.id);
    if (!permalink || permalink.includes('/p/') && post.media_type !== 'VIDEO') continue;
    const posted = instagramFeedTimestamp(post.posted_at);
    if (!Number.isFinite(posted) || posted > now + 300000) continue;
    return { id: post.id, permalink, coverAsset: post.cover_asset, videoUrl: hostedInstagramVideo(post.video_url), latest: i === 0 && raw.latest_verified === true && fresh };
  }
  return null;
}

export function isInstagramDismissSwipe(start, end) {
  if (!start || !end || start.pointerId !== end.pointerId || !['touch', 'pen'].includes(start.pointerType) || end.time - start.time < 0 || end.time - start.time > 1200) return false;
  const dx = end.x - start.x, dy = end.y - start.y;
  return dx <= -45 && Math.abs(dx) > Math.abs(dy) * 1.1 || dy >= 55 && Math.abs(dy) > Math.abs(dx) * 1.1;
}

export function floatingInstagramBusy(doc, win) {
  if (doc.hidden || doc.visibilityState === 'hidden' || win.visualViewport && win.visualViewport.height < 420) return true;
  if (doc.querySelector('[data-intro]:not([hidden]),dialog[open],[data-purchase-toast]:not([hidden])')) return true;
  const privacy = win.NLVPrivacy;
  if (doc.querySelector('[data-nlv-privacy]') && (!privacy?.ready || privacy.nativeVisible?.() || privacy.needsChoice?.())) return true;
  if (win.innerWidth <= 600 && doc.activeElement?.matches?.('input,textarea,select,[contenteditable="true"]')) return true;
  return false;
}

export function attachFloatingInstagram(host, options = {}) {
  const doc = host.ownerDocument, win = doc?.defaultView;
  const panel = host.querySelector('[data-floating-instagram-card]');
  const button = host.querySelector('[data-floating-instagram-open]');
  const poster = host.querySelector('[data-floating-instagram-poster]');
  const preview = host.querySelector('[data-floating-instagram-preview]');
  const template = host.querySelector('[data-video-template]');
  const dialog = host.querySelector('[data-video-dialog]');
  if (!win || !panel || !button || !poster || !template || !dialog) return { ready: Promise.resolve(false), dispose() {} };
  const listeners = [], request = new AbortController();
  const on = (target, type, callback, settings) => { target?.addEventListener?.(type, callback, settings); listeners.push(() => target?.removeEventListener?.(type, callback, settings)); };
  const now = options.now || (() => Date.now());
  const motion = win.matchMedia?.('(prefers-reduced-motion: reduce)') || { matches: true };
  let post, disposed = false, dismissed = false, delayReady = false, frame = null, start = null, suppressClickUntil = 0;
  const visible = node => Boolean(node && !node.hidden && node.getClientRects().length && win.getComputedStyle(node).display !== 'none' && win.getComputedStyle(node).visibility !== 'hidden');
  const stopPreview = (release = false) => {
    if (!preview) return;
    preview.pause();
    if (release) { preview.removeAttribute('src'); preview.load(); }
  };
  const overlapsPurchase = () => {
    const rect = panel.getBoundingClientRect();
    return [...doc.querySelectorAll('[data-add-button],.shopify-payment-button,[data-bale-add],[data-bale-submit],[name="checkout"],.cart-summary .button,.product-form .button')].filter(visible).some(control => {
      const bounds = control.getBoundingClientRect();
      return rect.left < bounds.right + 12 && rect.right > bounds.left - 12 && rect.top < bounds.bottom + 12 && rect.bottom > bounds.top - 12;
    });
  };
  const attempt = () => {
    frame = null;
    if (disposed || !post || dismissed || !delayReady || floatingInstagramBusy(doc, win)) { if (!panel.hidden) panel.hidden = true; stopPreview(); return; }
    const hidden = panel.hidden;
    if (hidden) { panel.style.visibility = 'hidden'; panel.hidden = false; }
    const blocked = overlapsPurchase();
    if (hidden) panel.style.visibility = '';
    panel.hidden = blocked;
    if (blocked || motion.matches || !post.videoUrl) { stopPreview(); return; }
    if (preview && preview.getAttribute('src') !== post.videoUrl) preview.src = post.videoUrl;
    if (preview?.paused) preview.play()?.catch?.(() => {});
  };
  const queueAttempt = () => { if (frame === null && !disposed) frame = win.requestAnimationFrame(attempt); };
  const dismiss = () => {
    if (!post || dismissed) return;
    dismissed = true; button.disabled = true;
    try { win.sessionStorage.setItem(DISMISS_PREFIX + post.id, '1'); } catch { /* This page still honors dismissal when storage is unavailable. */ }
    panel.hidden = true; stopPreview(true);
    if (panel.contains(doc.activeElement)) doc.getElementById('MainContent')?.focus({ preventScroll: true });
  };
  on(host.querySelector('[data-floating-instagram-close]'), 'click', dismiss);
  on(panel, 'keydown', event => { if (event.key === 'Escape') { event.preventDefault(); dismiss(); } });
  const releasePointer = () => { if (start) { try { button.releasePointerCapture?.(start.pointerId); } catch { /* A cancelled pointer can already be released. */ } } };
  on(button, 'pointerdown', event => {
    start = { x: event.clientX, y: event.clientY, pointerId: event.pointerId, pointerType: event.pointerType, time: now() };
    if (['touch', 'pen'].includes(event.pointerType)) { try { button.setPointerCapture?.(event.pointerId); } catch { /* Pointer cancellation remains safe. */ } }
  });
  on(button, 'pointerup', event => {
    if (isInstagramDismissSwipe(start, { x: event.clientX, y: event.clientY, pointerId: event.pointerId, time: now() })) { suppressClickUntil = now() + 500; dismiss(); }
    releasePointer(); start = null;
  });
  on(button, 'pointercancel', () => { releasePointer(); start = null; });
  // The shared privacy capture guard normally handles this. Keep the new card
  // fail-closed if that script is missing, rather than loading an Instagram SDK.
  on(win, 'click', event => {
    if (!host.contains(event.target)) return;
    if (!event.target.closest?.('[data-floating-instagram-open]')) return;
    if (now() < suppressClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (post && !post.videoUrl && !win.NLVPrivacy?.canEmbed?.()) {
      event.preventDefault(); event.stopImmediatePropagation(); win.NLVPrivacy?.open?.({ embeddedUrl: post.permalink });
    }
  }, true);
  on(host, 'click', queueAttempt);
  // Restore the visible opener before the shared player's bubble-phase close
  // handler returns keyboard focus to it.
  on(dialog, 'close', () => { if (frame !== null) win.cancelAnimationFrame(frame); frame = null; attempt(); }, { capture: true });
  on(dialog, 'toggle', queueAttempt);
  on(doc, 'nlv:privacy:ready', queueAttempt);
  on(doc, 'visitorConsentCollected', queueAttempt);
  on(doc, 'visibilitychange', queueAttempt);
  on(win, 'scroll', queueAttempt, { passive: true });
  on(win, 'resize', queueAttempt, { passive: true });
  on(win.visualViewport, 'resize', queueAttempt, { passive: true });
  on(motion, 'change', queueAttempt);
  const observer = typeof win.MutationObserver === 'function' ? new win.MutationObserver(records => { if (records.some(record => !panel.contains(record.target))) queueAttempt(); }) : null;
  observer?.observe(doc.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'open', 'style', 'class'] });
  const delay = win.setTimeout(() => { delayReady = true; queueAttempt(); }, 4500);
  const ready = (async () => {
    const feedUrl = instagramFeedUrl(host.dataset.instagramFeedUrl, win.location.href);
    if (!feedUrl) return false;
    try {
      const response = await (options.fetch || win.fetch.bind(win))(feedUrl, { credentials: 'omit', signal: request.signal, cache: 'no-cache' });
      if (!response.ok || disposed) return false;
      const validatedPost = validateInstagramFeed(await response.json(), now());
      if (!validatedPost || disposed) return false;
      const assetBase = instagramFeedUrl(host.dataset.instagramAssetBase, win.location.href);
      if (!assetBase) return false;
      const cover = new URL(validatedPost.coverAsset, assetBase).href;
      const instagram = template.content.querySelector('[data-instagram-permalink]');
      if (!instagram) return false;
      poster.src = cover;
      const external = host.querySelector('[data-floating-instagram-external]');
      if (external) external.href = validatedPost.permalink;
      const label = host.querySelector('[data-floating-instagram-label]');
      if (label) label.textContent = validatedPost.latest ? host.dataset.instagramLatestLabel : host.dataset.instagramLabel;
      const modalLink = template.content.querySelector('[data-floating-instagram-modal-link]');
      if (modalLink) modalLink.href = validatedPost.permalink;
      if (validatedPost.videoUrl) {
        const video = doc.createElement('video');
        video.className = 'nlv-video-player'; video.controls = true; video.playsInline = true; video.preload = 'none'; video.poster = cover; video.src = validatedPost.videoUrl;
        video.setAttribute('aria-label', button.getAttribute('aria-label'));
        instagram.replaceWith(video);
        if (preview) { preview.poster = cover; preview.muted = true; preview.loop = true; preview.playsInline = true; preview.hidden = false; }
      } else instagram.dataset.instagramPermalink = validatedPost.permalink;
      post = validatedPost;
      try { dismissed = win.sessionStorage.getItem(DISMISS_PREFIX + post.id) === '1'; } catch { /* Session storage is optional. */ }
      button.disabled = false; queueAttempt(); return true;
    } catch { return false; }
  })();
  return { ready, dispose() {
    disposed = true; releasePointer(); request.abort(); win.clearTimeout(delay); if (frame !== null) win.cancelAnimationFrame(frame);
    observer?.disconnect(); listeners.splice(0).forEach(remove => remove());
    host.dispatchEvent(new Event('nlv:video-cleanup'));
    panel.hidden = true; stopPreview(true);
  } };
}

const instances = new Map();
export function initFloatingInstagram(root = document) {
  const hosts = [...root.querySelectorAll('[data-floating-instagram]')];
  if (root.matches?.('[data-floating-instagram]')) hosts.unshift(root);
  for (const host of hosts) if (!instances.has(host)) instances.set(host, attachFloatingInstagram(host));
}
export function unloadFloatingInstagram(root) {
  for (const [host, instance] of instances) if (host === root || root?.contains?.(host)) { instance.dispose(); instances.delete(host); }
}
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initFloatingInstagram(document), { once: true });
  else initFloatingInstagram(document);
  document.addEventListener('shopify:section:load', event => initFloatingInstagram(event.target));
  document.addEventListener('shopify:section:unload', event => unloadFloatingInstagram(event.target));
}
